// Tonight's end screen (sprint 29 item 2): the squares, the score, fooled %, the day streak, and a button that copies the share line.
// DOM-light on purpose: `doc` makes the elements, so a test can hold it with a small fake.
import { fooledLine } from '../game/tonight.js';

// The link in the share line (sprint 31): /?tonight=1 opens tonight's door for whoever follows it; origin is the page's own (location.origin).
export const tonightUrl = origin => `${String(origin ?? '').replace(/\/+$/, '')}/?tonight=1`;
export const shareWithLink = (line, origin) => (origin ? `${line} ${tonightUrl(origin)}` : line);
export const wantsTonight = search => new URLSearchParams(search ?? '').get('tonight') === '1';

// Puts exactly `line` on the clipboard and tells the server once, only when it really got there.
export async function copyLine({ line, clipboard, send, fallback = () => false }) {
  let ok = false;
  try { if (clipboard?.writeText) { await clipboard.writeText(line); ok = true; } } catch { ok = false; }
  if (!ok) { try { ok = !!fallback(line); } catch { ok = false; } }
  if (ok) send({ t: 'tonight-copied' });
  return ok;
}

// The old way, for a page that may not use the clipboard API: a hidden field, selected and copied.
export function selectCopy(doc, text) {
  const field = doc.createElement('textarea');
  field.value = text; field.setAttribute('readonly', ''); field.style.position = 'fixed'; field.style.opacity = '0';
  doc.body.append(field); field.select();
  let ok = false;
  try { ok = doc.execCommand('copy'); } catch { ok = false; }
  field.remove();
  return ok;
}

export function renderTonight(root, m, { doc, clipboard, send, close, fallback, origin = '' }) {
  // every sentence sits in a .t span: the page's filter cuts its glyphs to ink or paper (tonight.css)
  const el = (tag, cls, text) => {
    const e = doc.createElement(tag);
    if (cls) e.className = cls;
    if (text) { const t = doc.createElement('span'); t.className = 't'; t.textContent = text; e.append(t); e.label = t; }
    return e;
  };
  const title = el('h2', 'tn-title');
  title.append(el('span', 'plate', `KNOCK #${m.number}`)); // paper under the striped bar, the glyphs on a layer of their own
  const squares = el('p', 'tn-squares');
  squares.setAttribute('aria-label', m.results.map(r => (r ? 'right' : 'wrong')).join(', '));
  for (const r of m.results) squares.append(el('span', r ? 'sq right' : 'sq wrong', r ? '■' : '□'));
  const score = el('p', 'tn-score', `${m.right}/${m.of}`);
  const fooled = el('p', 'tn-fooled', fooledLine(m.fooled));
  const list = el('ol', 'tn-doors');
  m.doors.forEach((d, i) => list.append(el('li', m.results[i] ? 'right' : 'wrong', `Door ${i + 1}: ${m.results[i] ? 'right' : 'wrong'}, fooled ${d.pct}%`)));
  const streak = el('p', 'tn-streak', `Day streak: ${m.streak}`);
  const note = el('p', 'tn-note', 'The share line is the squares and a link: no names, no lines, no visitors. The seed is on Verify after 00:00 UTC.');
  const copy = el('button', 'btn', 'Copy result'), done = el('button', 'btn', 'Close');
  copy.type = done.type = 'button';
  copy.onclick = async () => {
    const ok = await copyLine({ line: shareWithLink(m.line, origin), clipboard, send, fallback });
    copy.label.textContent = ok ? 'Copied' : 'Copy failed';
  };
  done.onclick = () => close();
  const verify = el('a', 'tn-verify', 'Verify the night');
  verify.href = `/verify?night=${m.day}`;
  const actions = el('div', 'tn-actions');
  actions.append(copy, done);
  const card = el('div', 'tn-card');
  card.append(title, squares, score, fooled, list, streak, note, actions, verify);
  root.replaceChildren(card);
  return { copy, done };
}
